import type { Manifest, ManifestType } from '../api/authoring'
import { typeOf } from './manifest'
import { asSchema, orderedProperties, resolve } from './schema'

// A tenant's component type definition, as this client reads and writes
// one.
//
// **These are the fields of a document, not an API shape**, which is why
// they are declared here rather than generated: the meta-type's fields
// are opaque to the contract exactly as every other type's are, so the
// contract describes them as raw JSON and the manifest carries their
// schema. This file is where that JSON gets a shape, on the same terms
// `model/schema.ts` gives the manifest's schemas one.

/** The field kinds the platform declares. Read off the wire, never listed. */
export interface FieldSpec {
  name: string
  kind: string
  required?: boolean
  /** A list's element kind. */
  of?: string
  /** An enum's closed vocabulary. */
  values?: string[]
  default?: unknown
  min?: number
  max?: number
  pattern?: string
}

/** One declared intent: what an author meant, and the fields it writes. */
export interface IntentSpec {
  name: string
  sets: string[]
  /** The reason a client may not command it, when it may not. */
  withheld?: string
  /** The words a control offers it under — "Add a photo" rather than a derived "Add image". */
  says?: string
}

/** One named relation a record of this type may hold. */
export interface RelationSpec {
  name: string
  accepts?: string[]
  acceptsRoles?: string[]
  many?: boolean
  /** The target belongs to the record, and is created with it. One named type, one target. */
  owned?: boolean
}

/**
 * What a type is called, as its own declaration holds it.
 *
 * The same three members the manifest publishes, because they are the
 * same declaration read from the other end: a tenant fills these in
 * here, and every client reads them off the manifest.
 */
export interface DeclaredProse {
  title?: string
  plural?: string
  description?: string
}

/** A whole declaration, as a `ComponentType` document holds it. */
export interface Declaration {
  typeName: string
  prose: DeclaredProse
  role?: string
  /** The fields that name a document, tried in order. */
  label?: string[]
  key?: string
  /** The Collection a new record of this type joins, by bare id. */
  collection?: string
  fields: FieldSpec[]
  references: RelationSpec[]
  contains?: string[]
  containsRoles?: string[]
  slots?: string[]
  intents: IntentSpec[]
}

/**
 * Read a definition out of a stored document's opaque fields.
 *
 * Everything absent becomes an empty list rather than undefined, because
 * every control below edits a list and none of them should have to ask
 * whether the member was there.
 */
export function declarationOf(fields: Record<string, unknown>): Declaration {
  return {
    typeName: typeof fields.typeName === 'string' ? fields.typeName : '',
    prose: (fields.prose ?? {}) as DeclaredProse,
    role: typeof fields.role === 'string' ? fields.role : '',
    label: Array.isArray(fields.label) ? (fields.label as string[]) : [],
    key: typeof fields.key === 'string' ? fields.key : '',
    collection: typeof fields.collection === 'string' ? fields.collection : '',
    fields: Array.isArray(fields.fields) ? (fields.fields as FieldSpec[]) : [],
    references: Array.isArray(fields.references) ? (fields.references as RelationSpec[]) : [],
    contains: Array.isArray(fields.contains) ? (fields.contains as string[]) : [],
    containsRoles: Array.isArray(fields.containsRoles) ? (fields.containsRoles as string[]) : [],
    slots: Array.isArray(fields.slots) ? (fields.slots as string[]) : [],
    intents: Array.isArray(fields.intents) ? (fields.intents as IntentSpec[]) : [],
  }
}

/**
 * The intent of a type that writes one named member.
 *
 * **Discovered, not listed.** The meta-type's intents have permanent wire
 * names, and this client deliberately does not hold them: it asks the
 * manifest which intent carries a `fields` member, the same way every
 * other control here asks which member is marked `x-slot`. What that
 * buys is not hypothetical — the type designer keeps working if the
 * platform splits or renames one of these intents, and it stops offering
 * a control the moment an intent is withheld, which is the behaviour
 * ground rule 8 asks of every client.
 */
export function intentWriting(
  manifest: Manifest | undefined,
  typeName: string,
  member: string,
): string | undefined {
  for (const intent of typeOf(manifest, typeName)?.intents ?? []) {
    const payload = asSchema(intent.payload)
    if (!payload) continue
    if (
      orderedProperties(payload, payload).some((property) => property.name === member)
    ) {
      return intent.name
    }
  }
  return undefined
}

/**
 * A closed vocabulary the meta-type's own schema declares — the field
 * kinds, and the roles.
 *
 * The platform owns both sets and publishes them as enums inside the
 * definition's fields schema, so the designer's selects are drawn from
 * the same declaration the validator reads. A list written here would be
 * the drift `content.Format` was made a named type to end.
 */
export function vocabularyAt(
  declared: ManifestType | undefined,
  path: string[],
): string[] {
  let schema = asSchema(declared?.fields)
  const root = schema
  if (!schema || !root) return []

  for (const step of path) {
    const resolved = resolve(schema, root)
    if (!resolved) return []
    const next = step === '[]' ? resolved.items : resolved.properties?.[step]
    schema = resolve(next, root)
    if (!schema) return []
  }
  // The empty string leads an optional vocabulary and means "not
  // stated"; the designer offers it as such rather than as a value.
  return schema.enum ?? []
}
